import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faCalendarPlus, faCheck, faChevronRight, faCircleDot, faClock, faArrowTurnDownRight, faPen, faSpinner, faPause, faFloppyDisk, faTrashCan, faXmark } from "@abodeminehq/parcel-ui/icons/fa/pro-solid";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  HStack,
  Input,
  Label,
  Separator,
  Skeleton,
  Spacer,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  VStack,
  cn,
} from "@abodeminehq/parcel-ui";
import * as Collapsible from "@radix-ui/react-collapsible";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { UpsertOrgQuotaForm } from "~/components/app/admin/upsert-org-quota-form";
import {
  SummeryContentLayout,
  SummeryContentLayoutAlert,
  SummeryContentLayoutContent,
  SummeryContentLayoutHeader,
} from "~/components/layouts/SummeryContentLayout";
import { useOrg } from "~/contexts/org-context";
import {
  useAllQuotasWithCurrent,
  useCurrentQuota,
  useDeleteQuota,
  useUpdateQuota,
} from "~/lib/queries/quotas";
import { OrgAlerts } from "~/components/app/shared/org-alerts";
import { useAuthScopes } from "~/hooks/use-auth-scopes";
import { LogtoScopes } from "~/lib/logto/logto";
import { useDynamicConfig } from "~/lib/queries/meta";
import { Quota } from "~/lib/schemas";

export const Route = createFileRoute("/admin/org/$org/quotas")({
  component: OrgQuotasPage,
});

/* ─── Color Palette for Quota Identity ─────────────────────────────── */

const QUOTA_COLORS = [
  { dot: "bg-emerald-500", ring: "ring-emerald-500/20", borderL: "border-l-emerald-500", bg: "bg-emerald-500/5", label: "text-emerald-600 dark:text-emerald-400" },
  { dot: "bg-violet-500", ring: "ring-violet-500/20", borderL: "border-l-violet-500", bg: "bg-violet-500/5", label: "text-violet-600 dark:text-violet-400" },
  { dot: "bg-amber-500", ring: "ring-amber-500/20", borderL: "border-l-amber-500", bg: "bg-amber-500/5", label: "text-amber-600 dark:text-amber-400" },
  { dot: "bg-sky-500", ring: "ring-sky-500/20", borderL: "border-l-sky-500", bg: "bg-sky-500/5", label: "text-sky-600 dark:text-sky-400" },
  { dot: "bg-rose-500", ring: "ring-rose-500/20", borderL: "border-l-rose-500", bg: "bg-rose-500/5", label: "text-rose-600 dark:text-rose-400" },
  { dot: "bg-indigo-500", ring: "ring-indigo-500/20", borderL: "border-l-indigo-500", bg: "bg-indigo-500/5", label: "text-indigo-600 dark:text-indigo-400" },
  { dot: "bg-orange-500", ring: "ring-orange-500/20", borderL: "border-l-orange-500", bg: "bg-orange-500/5", label: "text-orange-600 dark:text-orange-400" },
  { dot: "bg-teal-500", ring: "ring-teal-500/20", borderL: "border-l-teal-500", bg: "bg-teal-500/5", label: "text-teal-600 dark:text-teal-400" },
] as const;

type QuotaColor = (typeof QUOTA_COLORS)[number];
type QuotaStatus = "future" | "active" | "dormant" | "expired";

interface ClassifiedQuota {
  quota: Quota;
  status: QuotaStatus;
  color: QuotaColor;
}

interface ResumptionInfo {
  fallbackQuota: Quota;
  fallbackColor: QuotaColor;
}

interface OverrideInfo {
  overriddenBy: Quota;
  overrideColor: QuotaColor;
  resumesOn: Date | null;
}

function OrgQuotasPage() {
  const org = useOrg();
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [editingQuota, setEditingQuota] = useState<Quota | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotaToDelete, setQuotaToDelete] = useState<Quota | null>(null);

  const { data: quotas, isLoading } = useAllQuotasWithCurrent(
    org.activeOrg?.id,
    { sort_by: "priority", sort_order: "asc" },
  );
  const { data: currentQuota, isLoading: isCurrentLoading } = useCurrentQuota(
    org.activeOrg?.id,
  );
  const { data: dynamicConfig } = useDynamicConfig();
  const hasRequiredQuotaScopes = useAuthScopes(LogtoScopes.AdminOrgQuotaCrud);
  const deleteQuota = useDeleteQuota();

  const allQuotas = useMemo(() => quotas ?? [], [quotas]);

  const handleDelete = async () => {
    if (quotaToDelete) {
      try {
        await deleteQuota.mutateAsync({ quota_id: quotaToDelete.id });
        setDeleteDialogOpen(false);
        setQuotaToDelete(null);
      } catch (error) {
        console.error("Failed to delete quota:", error);
      }
    }
  };

  if (isLoading || isCurrentLoading || !dynamicConfig) {
    return (
      <SummeryContentLayout>
        <SummeryContentLayoutHeader
          title={org.activeOrg?.name ?? "Quotas"}
          subtitle="Quotas"
        />
        <SummeryContentLayoutContent>
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-3">
              <Skeleton className="h-10 w-40" />
              <Skeleton className="h-6 w-56" />
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 pt-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div className="space-y-3">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-6 w-48" />
                <div className="grid grid-cols-3 gap-4 pt-2">
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                </div>
              </div>
              <div className="space-y-3">
                <Skeleton className="h-10 w-36" />
                <Skeleton className="h-6 w-64" />
                <Skeleton className="h-20" />
                <Skeleton className="h-20" />
              </div>
            </div>
          </div>
        </SummeryContentLayoutContent>
      </SummeryContentLayout>
    );
  }

  return (
    <SummeryContentLayout>
      <SummeryContentLayoutHeader
        title={org.activeOrg?.name ?? "Quotas"}
        subtitle="Quotas"
      >
        {hasRequiredQuotaScopes && (
          <Dialog
            open={isScheduleDialogOpen}
            onOpenChange={setIsScheduleDialogOpen}
          >
            <DialogTrigger asChild>
              <Button>
                <FontAwesomeIcon icon={faCalendarPlus} className="mr-2 h-4 w-4" />
                Schedule Quota
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Schedule Future Quota</DialogTitle>
                <DialogDescription>
                  Create a new quota that will become active on a future date.
                </DialogDescription>
              </DialogHeader>
              <UpsertOrgQuotaForm
                existingQuota={undefined}
                onSuccess={() => setIsScheduleDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </SummeryContentLayoutHeader>

      <SummeryContentLayoutAlert>
        <OrgAlerts />
      </SummeryContentLayoutAlert>

      <SummeryContentLayoutContent>
        {dynamicConfig.usagePermissions.length > 8 ? (
          <div className="space-y-6">
            <PermissionsPanel
              currentQuota={currentQuota}
              dynamicConfig={dynamicConfig}
              canEdit={hasRequiredQuotaScopes}
            />
            <div className="grid gap-6 lg:grid-cols-2">
              <ActiveQuotaCard
                quota={currentQuota}
                canEdit={hasRequiredQuotaScopes}
              />
              <QuotaTimeline
                allQuotas={allQuotas}
                currentQuota={currentQuota}
                canEdit={hasRequiredQuotaScopes}
                onEdit={setEditingQuota}
                onDelete={(q) => {
                  setQuotaToDelete(q);
                  setDeleteDialogOpen(true);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            <PermissionsPanel
              currentQuota={currentQuota}
              dynamicConfig={dynamicConfig}
              canEdit={hasRequiredQuotaScopes}
              className="lg:col-span-2"
            />
            <div className="space-y-6">
              <ActiveQuotaCard
                quota={currentQuota}
                canEdit={hasRequiredQuotaScopes}
              />
              <QuotaTimeline
                allQuotas={allQuotas}
                currentQuota={currentQuota}
                canEdit={hasRequiredQuotaScopes}
                onEdit={setEditingQuota}
                onDelete={(q) => {
                  setQuotaToDelete(q);
                  setDeleteDialogOpen(true);
                }}
              />
            </div>
          </div>
        )}
      </SummeryContentLayoutContent>

      {/* Edit Dialog */}
      <Dialog
        open={editingQuota !== null}
        onOpenChange={(open) => {
          if (!open) setEditingQuota(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Quota</DialogTitle>
          </DialogHeader>
          {editingQuota && (
            <UpsertOrgQuotaForm
              existingQuota={editingQuota}
              onSuccess={() => setEditingQuota(null)}
              onCancel={() => setEditingQuota(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Quota</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this quota? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteQuota.isPending}
            >
              {deleteQuota.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SummeryContentLayout>
  );
}

/* ─── Permissions Panel ───────────────────────────────────────────── */

type PermissionItem = { key: string; friendlyName: string; description?: string; group?: string; isDeprecated?: boolean };

function PermissionRowLabel({ permission }: { permission: PermissionItem }) {
  if (permission.description) {
    return (
      <HoverCard openDelay={200}>
        <HoverCardTrigger asChild>
          <Label
            htmlFor={`perm-${permission.key}`}
            className="text-sm font-normal cursor-pointer underline decoration-dotted decoration-muted-foreground/50 underline-offset-4"
          >
            {permission.friendlyName}
          </Label>
        </HoverCardTrigger>
        <HoverCardContent className="w-64" align="start">
          <p className="text-sm text-muted-foreground">{permission.description}</p>
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <Label
      htmlFor={`perm-${permission.key}`}
      className="text-sm font-normal cursor-pointer"
    >
      {permission.friendlyName}
    </Label>
  );
}

function PermissionsPanel({
  currentQuota,
  dynamicConfig,
  canEdit,
  className,
}: {
  currentQuota: Quota | null | undefined;
  dynamicConfig: { usagePermissions: PermissionItem[] };
  canEdit: boolean;
  className?: string;
}) {
  const updateQuota = useUpdateQuota();
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>(
    {},
  );
  const [isSaving, setIsSaving] = useState(false);
  const [showDeprecated, setShowDeprecated] = useState(false);

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const handleToggle = useCallback(
    (key: string, checked: boolean) => {
      if (!currentQuota) return;
      const originalValue = (currentQuota as any)[key];
      if (checked === originalValue) {
        setPendingChanges((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      } else {
        setPendingChanges((prev) => ({ ...prev, [key]: checked }));
      }
    },
    [currentQuota],
  );

  const handleSave = async () => {
    if (!currentQuota || !hasPendingChanges) return;
    setIsSaving(true);
    try {
      await updateQuota.mutateAsync({
        quota_id: currentQuota.id,
        ...pendingChanges,
        daily_parcels: currentQuota.daily_parcels,
        monthly_parcels: currentQuota.monthly_parcels,
        priority: currentQuota.priority,
        start_date: currentQuota.start_date,
        end_date: currentQuota.end_date,
        enable_expiration_reminder:
          currentQuota.enable_expiration_reminder ?? true,
      });
      setPendingChanges({});
    } catch (error) {
      console.error("Failed to update permissions:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => setPendingChanges({});

  const visiblePermissions = useMemo(() => {
    if (showDeprecated) return dynamicConfig.usagePermissions;
    return dynamicConfig.usagePermissions.filter((p) => !p.isDeprecated);
  }, [dynamicConfig.usagePermissions, showDeprecated]);

  const groups = useMemo(() => {
    const result: { label: string; key: string; perms: PermissionItem[] }[] = [];
    const groupMap = new Map<string, PermissionItem[]>();

    for (const perm of visiblePermissions) {
      const group = perm.group || '';
      const list = groupMap.get(group) ?? [];
      list.push(perm);
      groupMap.set(group, list);
    }

    for (const [key, perms] of groupMap) {
      result.push({ label: key, key: key || '__ungrouped__', perms });
    }

    return result;
  }, [visiblePermissions]);

  if (!currentQuota) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base">Permissions</CardTitle>
          <CardDescription>No active quota</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Create a quota to configure API permissions.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("h-fit", className)}>
      <CardHeader className="pb-3">
        <HStack>
          <div>
            <CardTitle className="text-base">Permissions</CardTitle>
            <CardDescription>Active quota access controls</CardDescription>
          </div>
          <Spacer />
          {hasPendingChanges && (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faFloppyDisk} className="mr-2 h-3.5 w-3.5" />
                )}
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDiscard}>
                Discard
              </Button>
            </div>
          )}
        </HStack>
      </CardHeader>
      <CardContent>
        <div className="flex justify-end mb-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="panel-show-deprecated" className="text-xs text-muted-foreground font-normal cursor-pointer">
              Show deprecated
            </Label>
            <Switch
              id="panel-show-deprecated"
              checked={showDeprecated}
              onCheckedChange={setShowDeprecated}
              className="scale-75"
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3">
          {groups.map(({ label, key: groupKey, perms }) => {
            const hasLabel = label && groupKey !== '__ungrouped__';

            return (
              <Fragment key={groupKey}>
                {hasLabel && (
                  <Collapsible.Root defaultOpen className="col-span-full">
                    <Collapsible.Trigger className="flex items-center gap-1 group cursor-pointer py-1.5 px-1">
                      <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
                      <span className="text-xs font-medium text-muted-foreground">
                        {label}
                      </span>
                    </Collapsible.Trigger>
                    <Collapsible.Content className="grid sm:grid-cols-2 lg:grid-cols-3 col-span-full">
                      {perms.map((permission, rowIdx) => {
                        const currentValue =
                          pendingChanges[permission.key] ??
                          (currentQuota as any)[permission.key] ??
                          false;
                        const isChanged = permission.key in pendingChanges;

                        return (
                          <div
                            key={permission.key}
                            className={cn(
                              "flex items-center justify-between px-3 py-2.5 transition-colors",
                              isChanged
                                ? "bg-accent/50"
                                : rowIdx % 2 === 0
                                  ? "bg-muted/30"
                                  : "",
                              permission.isDeprecated && "opacity-60",
                            )}
                          >
                            <PermissionRowLabel permission={permission} />
                            <Switch
                              id={`perm-${permission.key}`}
                              checked={currentValue}
                              onCheckedChange={(checked) =>
                                handleToggle(permission.key, !!checked)
                              }
                              disabled={!canEdit}
                            />
                          </div>
                        );
                      })}
                    </Collapsible.Content>
                  </Collapsible.Root>
                )}
                {!hasLabel && perms.map((permission, rowIdx) => {
                  const currentValue =
                    pendingChanges[permission.key] ??
                    (currentQuota as any)[permission.key] ??
                    false;
                  const isChanged = permission.key in pendingChanges;

                  return (
                    <div
                      key={permission.key}
                      className={cn(
                        "flex items-center justify-between px-3 py-2.5 transition-colors",
                        isChanged
                          ? "bg-accent/50"
                          : rowIdx % 2 === 0
                            ? "bg-muted/30"
                            : "",
                        permission.isDeprecated && "opacity-60",
                      )}
                    >
                      <PermissionRowLabel permission={permission} />
                      <Switch
                        id={`perm-${permission.key}`}
                        checked={currentValue}
                        onCheckedChange={(checked) =>
                          handleToggle(permission.key, !!checked)
                        }
                        disabled={!canEdit}
                      />
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Active Quota Card ───────────────────────────────────────────── */

function toDateInputValue(date: Date | null | undefined): string {
  if (!date) return "";
  return date.toISOString().split("T")[0];
}

function ActiveQuotaCard({
  quota,
  canEdit,
}: {
  quota: Quota | null | undefined;
  canEdit: boolean;
}) {
  const updateQuota = useUpdateQuota();
  const [isEditing, setIsEditing] = useState(false);
  const [dailyParcels, setDailyParcels] = useState<number | null>(null);
  const [monthlyParcels, setMonthlyParcels] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  useEffect(() => {
    if (quota) {
      setDailyParcels(quota.daily_parcels);
      setMonthlyParcels(quota.monthly_parcels);
      setStartDate(toDateInputValue(quota.start_date));
      setEndDate(toDateInputValue(quota.end_date));
    }
  }, [quota]);

  const handleSave = async () => {
    if (!quota) return;
    try {
      await updateQuota.mutateAsync({
        quota_id: quota.id,
        daily_parcels: dailyParcels,
        monthly_parcels: monthlyParcels,
        priority: quota.priority,
        start_date: startDate ? new Date(startDate) : quota.start_date,
        end_date: endDate ? new Date(endDate) : null,
        enable_expiration_reminder: quota.enable_expiration_reminder ?? true,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update quota:", error);
    }
  };

  if (!quota) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <FontAwesomeIcon icon={faCircleDot} className="h-10 w-10 text-muted-foreground/40 mb-3" />
          <p className="font-medium">No Active Quota</p>
          <p className="text-sm text-muted-foreground mt-1">
            Schedule a quota to enable API access for this organization.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <HStack>
          <div>
            <HStack spacing={2}>
              <CardTitle className="text-base">Current Quota</CardTitle>
              <Badge variant="green">Active</Badge>
            </HStack>
            <CardDescription>
              {quota.start_date.toLocaleDateString()} —{" "}
              {quota.end_date?.toLocaleDateString() ?? "No end date"}
            </CardDescription>
          </div>
          <div className="ml-auto">
            {canEdit && !isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <FontAwesomeIcon icon={faPen} className="h-4 w-4" />
              </Button>
            )}
          </div>
        </HStack>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Daily Limit</p>
            {isEditing ? (
              <Input
                type="number"
                value={dailyParcels ?? ""}
                onChange={(e) =>
                  setDailyParcels(
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                className="h-8"
              />
            ) : (
              <p className="text-lg font-semibold tabular-nums">
                {quota.daily_parcels?.toLocaleString() ?? "∞"}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Monthly Limit</p>
            {isEditing ? (
              <Input
                type="number"
                value={monthlyParcels ?? ""}
                onChange={(e) =>
                  setMonthlyParcels(
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                className="h-8"
              />
            ) : (
              <p className="text-lg font-semibold tabular-nums">
                {quota.monthly_parcels?.toLocaleString() ?? "∞"}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Priority</p>
            <p className="text-lg font-semibold tabular-nums">
              {quota.priority}
            </p>
          </div>
        </div>

        {isEditing && (
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Start Date</p>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-8"
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">End Date</p>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-8"
              />
            </div>
          </div>
        )}

        {isEditing && (
          <div className="flex gap-2 mt-4">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={updateQuota.isPending}
            >
              {updateQuota.isPending ? (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faFloppyDisk} className="mr-2 h-3.5 w-3.5" />
              )}
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setDailyParcels(quota.daily_parcels);
                setMonthlyParcels(quota.monthly_parcels);
                setStartDate(toDateInputValue(quota.start_date));
                setEndDate(toDateInputValue(quota.end_date));
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Quota Timeline ──────────────────────────────────────────────── */

function useTimelineData(allQuotas: Quota[], currentQuota: Quota | null | undefined) {
  const now = new Date();

  const colorMap = useMemo(() => {
    const map = new Map<string, QuotaColor>();
    const sorted = [...allQuotas].sort(
      (a, b) => b.priority - a.priority || a.start_date.getTime() - b.start_date.getTime(),
    );
    sorted.forEach((q, i) => {
      map.set(q.id, QUOTA_COLORS[i % QUOTA_COLORS.length]);
    });
    return map;
  }, [allQuotas]);

  const classified = useMemo<ClassifiedQuota[]>(() => {
    return allQuotas.map((q) => {
      let status: QuotaStatus;
      if (q.is_current || q.id === currentQuota?.id) {
        status = "active";
      } else if (q.start_date > now) {
        status = "future";
      } else if (q.end_date && q.end_date <= now) {
        status = "expired";
      } else {
        status = "dormant";
      }
      return { quota: q, status, color: colorMap.get(q.id) ?? QUOTA_COLORS[0] };
    });
  }, [allQuotas, currentQuota, colorMap]);

  const resumptionMap = useMemo(() => {
    const map = new Map<string, ResumptionInfo>();
    for (const { quota } of classified) {
      if (!quota.end_date) continue;
      const candidates = allQuotas
        .filter(
          (q) =>
            q.id !== quota.id &&
            q.start_date <= quota.end_date! &&
            (q.end_date == null || q.end_date > quota.end_date!),
        )
        .sort((a, b) => b.priority - a.priority);

      if (candidates.length > 0) {
        map.set(quota.id, {
          fallbackQuota: candidates[0],
          fallbackColor: colorMap.get(candidates[0].id) ?? QUOTA_COLORS[0],
        });
      }
    }
    return map;
  }, [classified, allQuotas, colorMap]);

  const overrideMap = useMemo(() => {
    const map = new Map<string, OverrideInfo>();
    for (const { quota, status } of classified) {
      if (status !== "dormant") continue;
      const overriders = allQuotas
        .filter(
          (q) =>
            q.id !== quota.id &&
            q.priority > quota.priority &&
            q.start_date <= now &&
            (q.end_date == null || q.end_date > now),
        )
        .sort((a, b) => b.priority - a.priority);

      if (overriders.length > 0) {
        const overrider = overriders[0];
        map.set(quota.id, {
          overriddenBy: overrider,
          overrideColor: colorMap.get(overrider.id) ?? QUOTA_COLORS[0],
          resumesOn: overrider.end_date ?? null,
        });
      }
    }
    return map;
  }, [classified, allQuotas, colorMap]);

  const future = useMemo(
    () =>
      classified
        .filter((c) => c.status === "future")
        .sort((a, b) => a.quota.start_date.getTime() - b.quota.start_date.getTime()),
    [classified],
  );
  const active = useMemo(() => classified.filter((c) => c.status === "active"), [classified]);
  const dormant = useMemo(
    () =>
      classified
        .filter((c) => c.status === "dormant")
        .sort((a, b) => b.quota.priority - a.quota.priority),
    [classified],
  );
  const expired = useMemo(
    () =>
      classified
        .filter((c) => c.status === "expired")
        .sort((a, b) => b.quota.start_date.getTime() - a.quota.start_date.getTime()),
    [classified],
  );

  return { colorMap, future, active, dormant, expired, resumptionMap, overrideMap };
}

function QuotaTimeline({
  allQuotas,
  currentQuota,
  canEdit,
  onEdit,
  onDelete,
}: {
  allQuotas: Quota[];
  currentQuota: Quota | null | undefined;
  canEdit: boolean;
  onEdit: (quota: Quota) => void;
  onDelete: (quota: Quota) => void;
}) {
  const [showExpired, setShowExpired] = useState(false);
  const { future, active, dormant, expired, resumptionMap, overrideMap } =
    useTimelineData(allQuotas, currentQuota);

  const hasAnyQuotas = allQuotas.length > 0;
  if (!hasAnyQuotas) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Quota Timeline</CardTitle>
        <CardDescription>
          Scheduled, active, dormant, and past quotas — color-coded by identity
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative pl-6 space-y-0">
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />

          {/* Future quotas */}
          {future.map(({ quota, status, color }) => (
            <TimelineItem
              key={quota.id}
              quota={quota}
              status={status}
              color={color}
              resumption={resumptionMap.get(quota.id)}
              canEdit={canEdit}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}

          {/* Active quota */}
          {active.map(({ quota, status, color }) => (
            <TimelineItem
              key={quota.id}
              quota={quota}
              status={status}
              color={color}
              resumption={resumptionMap.get(quota.id)}
              canEdit={false}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}

          {/* Dormant quotas */}
          {dormant.length > 0 && (
            <>
              <div className="relative py-2">
                <div className="absolute -left-6 top-1/2 -translate-y-1/2 flex items-center justify-center w-[22px]">
                  <FontAwesomeIcon icon={faPause} className="h-3 w-3 text-muted-foreground/60" />
                </div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Dormant — overridden by higher priority
                </p>
              </div>
              {dormant.map(({ quota, status, color }) => (
                <TimelineItem
                  key={quota.id}
                  quota={quota}
                  status={status}
                  color={color}
                  override={overrideMap.get(quota.id)}
                  canEdit={canEdit}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </>
          )}

          {/* Expired quotas toggle */}
          {expired.length > 0 && (
            <>
              <button
                onClick={() => setShowExpired(!showExpired)}
                className="relative flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <div className="absolute -left-6 flex items-center justify-center w-[22px]">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                </div>
                <FontAwesomeIcon icon={faChevronRight}
                  className={cn(
                    "h-3.5 w-3.5 transition-transform",
                    showExpired && "rotate-90",
                  )}
                />
                {expired.length} expired quota{expired.length !== 1 && "s"}
              </button>

              {showExpired &&
                expired.map(({ quota, status, color }) => (
                  <TimelineItem
                    key={quota.id}
                    quota={quota}
                    status={status}
                    color={color}
                    canEdit={canEdit}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Timeline Item ──────────────────────────────────────────────── */

function TimelineItem({
  quota,
  status,
  color,
  resumption,
  override,
  canEdit,
  onEdit,
  onDelete,
}: {
  quota: Quota;
  status: QuotaStatus;
  color: QuotaColor;
  resumption?: ResumptionInfo;
  override?: OverrideInfo;
  canEdit: boolean;
  onEdit: (quota: Quota) => void;
  onDelete: (quota: Quota) => void;
}) {
  const statusBadge: Record<QuotaStatus, { variant: "green" | "blue" | "secondary" | "yellow"; label: string }> = {
    future: { variant: "blue", label: "Scheduled" },
    active: { variant: "green", label: "Active" },
    dormant: { variant: "yellow", label: "Dormant" },
    expired: { variant: "secondary", label: "Expired" },
  };

  const { variant, label } = statusBadge[status];

  return (
    <div className="relative py-2 group">
      {/* Color-coded dot */}
      <div className="absolute -left-6 top-[16px] flex items-center justify-center w-[22px]">
        <div
          className={cn(
            "h-2.5 w-2.5 rounded-full",
            color.dot,
            status === "active" && `ring-4 ${color.ring}`,
          )}
        />
      </div>

      {/* Color-coded card */}
      <div
        className={cn(
          "rounded-lg border border-l-[3px] px-4 py-3 transition-colors",
          color.borderL,
          status === "active" && color.bg,
          status === "future" && "bg-muted/30",
          status === "dormant" && "bg-muted/20 opacity-75",
          status === "expired" && "opacity-50",
        )}
      >
        <HStack>
          <div className="flex-1 min-w-0">
            <HStack spacing={2} className="mb-1 flex-wrap">
              <Badge variant={variant} className="text-xs">
                {label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Priority {quota.priority}
              </span>
              {quota.end_date && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                  Has end date
                </span>
              )}
              <Spacer />
            </HStack>
            <p className="text-sm">
              <span className="font-medium">
                {quota.start_date.toLocaleDateString()}
              </span>
              <span className="text-muted-foreground"> — </span>
              <span className="font-medium">
                {quota.end_date?.toLocaleDateString() ?? "No end date"}
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Daily: {quota.daily_parcels?.toLocaleString() ?? "∞"} · Monthly:{" "}
              {quota.monthly_parcels?.toLocaleString() ?? "∞"}
            </p>

            {/* Resumption annotation: what takes over when this one expires */}
            {resumption && (
              <div className="mt-2 flex items-center gap-1.5 text-xs">
                <FontAwesomeIcon icon={faArrowTurnDownRight} className={cn("h-3.5 w-3.5 shrink-0", resumption.fallbackColor.label)} />
                <span className="text-muted-foreground">
                  After this expires →{" "}
                  <span className={cn("font-medium", resumption.fallbackColor.label)}>
                    Priority {resumption.fallbackQuota.priority} quota
                  </span>{" "}
                  resumes
                </span>
              </div>
            )}

            {/* Override annotation: what's blocking this dormant quota */}
            {override && (
              <div className="mt-2 flex items-center gap-1.5 text-xs">
                <FontAwesomeIcon icon={faPause} className={cn("h-3.5 w-3.5 shrink-0", override.overrideColor.label)} />
                <span className="text-muted-foreground">
                  Overridden by{" "}
                  <span className={cn("font-medium", override.overrideColor.label)}>
                    Priority {override.overriddenBy.priority} quota
                  </span>
                  {override.resumesOn ? (
                    <> · Resumes {override.resumesOn.toLocaleDateString()}</>
                  ) : (
                    <> · No scheduled resumption</>
                  )}
                </span>
              </div>
            )}
          </div>

          {canEdit && (
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => onEdit(quota)}
                  >
                    <FontAwesomeIcon icon={faPen} className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => onDelete(quota)}
                  >
                    <FontAwesomeIcon icon={faTrashCan} className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </div>
          )}
        </HStack>
      </div>
    </div>
  );
}
